// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Puestos List Page
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../../../i18n';
import {
  fetchPuestos,
  fetchDepartamentos,
  fetchNivelesPuesto,
} from '../../../../shared/services/api';
import type { NivelPuesto } from '../../../../shared/services/api';
import { FichaPersonalViewer } from '../../components/FichaPersonalViewer';
import { NIVEL_FALLBACK } from '../../../../../lib/nivel-style';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { InfoTooltip } from '@/components/ui/InfoTooltip';
import { PersonaViewerModal } from '../../components/PersonaViewerModal';
import { PuestoViewerModal } from '../../components/PuestoViewerModal';

interface Puesto {
  id: string;
  titulo: string;
  nivel: string | null;
  nivelPuesto?: { nombre?: string; color?: string | null; colorFondo?: string | null } | null;
  departamento?: { id: string; nombre: string } | null;
  persona?: { id: string; nombre: string; apellidos: string } | null;
  fichaDocumentoId?: string | null;
  rolFuncional?: { id: string; nombre: string; fichaDocumentoId: string | null } | null;
}

interface Departamento {
  id: string;
  nombre: string;
  codigo: string;
}

interface PuestosListPageProps {
  embedded?: boolean;
}

export function PuestosListPage({ embedded }: PuestosListPageProps) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const st = t.structure as any;
  const adm = st?.admin ?? {};

  const [puestos, setPuestos] = useState<Puesto[]>([]);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [, setNivelesConfig] = useState<NivelPuesto[]>([]);
  // 0615k: viewer-only modal de persona (link de la persona asignada en tabla).
  const [viewerPersonaId, setViewerPersonaId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [nivelFilter, setNivelFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [soloVacantes, setSoloVacantes] = useState(false);

  // 0615l: viewer-only modal de Puesto (sustituye al drawer lateral previo).
  const [viewerPuestoId, setViewerPuestoId] = useState<string | null>(null);
  const [fichaViewerPuesto, setFichaViewerPuesto] = useState<Puesto | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [pRes, dRes, nRes] = await Promise.all([
        fetchPuestos({ porPagina: '500' }),
        fetchDepartamentos({ porPagina: '100' }),
        fetchNivelesPuesto(),
      ]);
      // 0618 fix: el endpoint list devuelve asignaciones[] no persona directa.
      // Normalizamos para que la tabla muestre la persona ocupante (no "Vacante" siempre).
      const puestosNorm = (
        (pRes.data ?? []) as unknown as Array<
          Puesto & { asignaciones?: Array<{ activo: boolean; persona?: Puesto['persona'] }> }
        >
      ).map((p) => ({
        ...p,
        persona: p.persona ?? p.asignaciones?.find((a) => a.activo)?.persona ?? null,
      }));
      setPuestos(puestosNorm as Puesto[]);
      setDepartamentos(dRes.data ?? []);
      setNivelesConfig(nRes.data ?? []);
    } catch (err) {
      console.error('Error loading puestos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 0619 fix: niveles derivados de nivelPuesto (FK actual con nombre legible)
  // y no de p.nivel (slug legacy = null en BD nueva). Antes el dropdown salia
  // vacio porque todos los p.nivel eran null. value=nombre para que coincida
  // exactamente con el badge mostrado en la columna Nivel (consistencia).
  const niveles = useMemo(() => {
    const map = new Map<string, string>(); // nombre -> nombre (dedupe)
    for (const p of puestos) {
      const nombre =
        (p.nivelPuesto as { nombre?: string } | null | undefined)?.nombre ??
        (p.nivel ? p.nivel.replace(/_/g, ' ') : null);
      if (nombre) map.set(nombre, nombre);
    }
    return Array.from(map.values()).sort();
  }, [puestos]);

  const filtered = useMemo(() => {
    let result = puestos;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.titulo.toLowerCase().includes(q) ||
          p.persona?.nombre?.toLowerCase().includes(q) ||
          p.persona?.apellidos?.toLowerCase().includes(q) ||
          p.departamento?.nombre?.toLowerCase().includes(q),
      );
    }
    if (nivelFilter) {
      // 0619: comparar contra el nombre del nivelPuesto (FK actual) o slug legacy.
      result = result.filter((p) => {
        const nombre =
          (p.nivelPuesto as { nombre?: string } | null | undefined)?.nombre ??
          (p.nivel ? p.nivel.replace(/_/g, ' ') : null);
        return nombre === nivelFilter;
      });
    }
    if (deptFilter) {
      result = result.filter((p) => p.departamento?.id === deptFilter);
    }
    if (soloVacantes) {
      result = result.filter((p) => !p.persona);
    }
    return result;
  }, [puestos, search, nivelFilter, deptFilter, soloVacantes]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 48 }}>
        <LoadingSpinner size="md" muted />
      </div>
    );
  }

  return (
    <div style={{ padding: embedded ? 0 : 24, fontFamily: "'DM Sans', sans-serif" }}>
      {!embedded && (
        <h1
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: '#1F3864',
            margin: '0 0 16px 0',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {t.structure.puestosTitle}
          <InfoTooltip
            title={t.structure.tooltips.puestos.titulo}
            text={t.structure.tooltips.puestos.ayuda}
          />
        </h1>
      )}
      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginBottom: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <input
          type="text"
          placeholder={adm.search ?? 'Buscar...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: '8px 14px',
            borderRadius: 8,
            border: '1px solid #D1D5DB',
            fontSize: 13,
            flex: '1 1 200px',
            minWidth: 180,
            outline: 'none',
            fontFamily: 'inherit',
          }}
        />
        <select
          value={nivelFilter}
          onChange={(e) => setNivelFilter(e.target.value)}
          style={{
            padding: '8px 14px',
            borderRadius: 8,
            border: '1px solid #D1D5DB',
            fontSize: 13,
            fontFamily: 'inherit',
            background: '#fff',
            cursor: 'pointer',
          }}
        >
          <option value="">
            {st?.nivelLabel ?? 'Nivel'} — {st?.selectOption ?? 'Todos'}
          </option>
          {niveles.map((n) => (
            // 0619: el item ya viene normalizado (nombre legible o slug→texto)
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <select
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
          style={{
            padding: '8px 14px',
            borderRadius: 8,
            border: '1px solid #D1D5DB',
            fontSize: 13,
            fontFamily: 'inherit',
            background: '#fff',
            cursor: 'pointer',
          }}
        >
          <option value="">
            {st?.departamentoLabel ?? 'Departamento'} — {st?.selectOption ?? 'Todos'}
          </option>
          {departamentos.map((d) => (
            <option key={d.id} value={d.id}>
              {d.nombre}
            </option>
          ))}
        </select>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <input
            type="checkbox"
            checked={soloVacantes}
            onChange={(e) => setSoloVacantes(e.target.checked)}
            style={{ accentColor: '#1F3864' }}
          />
          {st?.soloVacantes ?? 'Solo vacantes'}
        </label>
      </div>

      {/* Table */}
      <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#0E243E', color: '#fff' }}>
              <th style={thStyle}>{st?.tituloLabel ?? 'Titulo'}</th>
              <th style={thStyle}>{st?.nivelLabel ?? 'Nivel'}</th>
              <th style={thStyle}>{st?.departamentoLabel ?? 'Departamento'}</th>
              <th style={thStyle}>{st?.drawer?.assignedPerson ?? 'Persona asignada'}</th>
              <th style={{ ...thStyle, width: 80, textAlign: 'center' }}>{t.common.estado}</th>
              <th style={{ ...thStyle, width: 60, textAlign: 'center' }}>
                {st?.fichaLabel ?? 'Ficha'}
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#9CA3AF' }}>
                  {adm.noResults ?? 'Sin resultados'}
                </td>
              </tr>
            ) : (
              filtered.map((p, i) => {
                const npx = p.nivelPuesto as
                  | { color?: string | null; colorFondo?: string | null; nombre?: string }
                  | undefined;
                // 0618 fix: detectar nivel desde nivelPuesto (FK actual) o nivel (slug legacy).
                // Antes solo miraba p.nivel y como BD nueva solo rellena nivelPuestoId,
                // todos salian "—".
                const tieneNivel = !!(npx?.nombre || p.nivel);
                const ns = npx?.color
                  ? { color: npx.color, bg: npx.colorFondo ?? NIVEL_FALLBACK.bg }
                  : tieneNivel
                    ? NIVEL_FALLBACK
                    : null;
                const labelNivel = npx?.nombre ?? (p.nivel ? p.nivel.replace(/_/g, ' ') : null);
                return (
                  <tr
                    key={p.id}
                    onClick={() => setViewerPuestoId(p.id)}
                    style={{
                      background: i % 2 === 0 ? '#fff' : '#F9FAFB',
                      borderBottom: '1px solid #F3F4F6',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#F0F4FF')}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#F9FAFB')
                    }
                  >
                    {/* Titulo */}
                    <td style={{ ...tdStyle, fontWeight: 600, color: '#1F3864' }}>{p.titulo}</td>
                    {/* Nivel badge */}
                    <td style={tdStyle}>
                      {tieneNivel && ns && labelNivel ? (
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '3px 10px',
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 600,
                            color: ns.color,
                            background: ns.bg,
                          }}
                        >
                          {labelNivel}
                        </span>
                      ) : (
                        <span style={{ color: '#9CA3AF' }}>—</span>
                      )}
                    </td>
                    {/* Departamento */}
                    <td style={tdStyle}>
                      {p.departamento?.nombre ?? <span style={{ color: '#9CA3AF' }}>—</span>}
                    </td>
                    {/* Persona */}
                    <td style={tdStyle}>
                      {p.persona ? (
                        <span
                          // 0615k: viewer-only modal de persona en lugar de navegar a admin.
                          // 0615l: stopPropagation para no abrir tambien el modal de puesto.
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewerPersonaId(p.persona!.id);
                          }}
                          style={{ color: 'var(--color-secundario, #2F5496)', cursor: 'pointer' }}
                          onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                        >
                          {p.persona.nombre} {p.persona.apellidos}
                        </span>
                      ) : (
                        <span style={{ color: '#D97706', fontSize: 11, fontWeight: 600 }}>
                          {st?.vacantLabel ?? 'Vacante'}
                        </span>
                      )}
                    </td>
                    {/* Estado badge */}
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {p.persona ? (
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 10,
                            fontSize: 10,
                            fontWeight: 600,
                            color: '#16A34A',
                            background: '#F0FDF4',
                          }}
                        >
                          {st?.ocupadoLabel ?? 'Ocupado'}
                        </span>
                      ) : (
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 10,
                            fontSize: 10,
                            fontWeight: 600,
                            color: '#DC2626',
                            background: '#FEF2F2',
                          }}
                        >
                          {st?.vacantLabel ?? 'Vacante'}
                        </span>
                      )}
                    </td>
                    {/* Ficha */}
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {p.fichaDocumentoId ? (
                        <svg
                          width={16}
                          height={16}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#16A34A"
                          strokeWidth={2}
                          strokeLinecap="round"
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                        </svg>
                      ) : (
                        <span style={{ color: '#D1D5DB' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer count */}
      <div style={{ marginTop: 10, fontSize: 12, color: '#9CA3AF', textAlign: 'right' }}>
        {filtered.length} / {puestos.length} {adm.puestos ?? 'puestos'}
      </div>

      {/* 0615l: viewer-only modal de Puesto (sustituye al drawer lateral). */}
      <PuestoViewerModal
        puestoId={viewerPuestoId}
        onClose={() => setViewerPuestoId(null)}
        onVerFicha={(id) => {
          const p = puestos.find((x) => x.id === id);
          if (p) {
            setFichaViewerPuesto(p);
            setViewerPuestoId(null);
          }
        }}
        onVerOrganigrama={(id) => {
          setViewerPuestoId(null);
          navigate(`/structure?tab=organigrama&focusPuesto=${id}`);
        }}
      />

      {/* FichaPersonalViewer modal (prompt 368) — 0618: viewer-only desde tab Puestos.
          Filosofia CEO: las tabs operativas no editan, edicion solo en Administracion.
          Sin prop `editable` -> oculta boton "Enviar a revision" + textareas read-only. */}
      {fichaViewerPuesto && (
        <FichaPersonalViewer
          tipo="FP"
          puestoId={fichaViewerPuesto.id}
          puestoTitulo={fichaViewerPuesto.titulo}
          puestoNivel={fichaViewerPuesto.nivel}
          rolFuncional={fichaViewerPuesto.rolFuncional}
          onClose={() => setFichaViewerPuesto(null)}
        />
      )}

      {/* 0615k: viewer-only modal de persona (link en columna persona). */}
      <PersonaViewerModal personaId={viewerPersonaId} onClose={() => setViewerPersonaId(null)} />
    </div>
  );
}

// ─── Shared styles ───
const thStyle: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: 12,
  letterSpacing: 0.3,
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px',
};
